import { copyResult } from '../../utils/analytics';
import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import CodeExportModal from '../../components/CodeExportModal';
import StudioToolbar from './StudioToolbar';
import StudioColorBar from './StudioColorBar';
import {
  generateRandomPalette,
  regeneratePalette,
  reorderPalette,
  parsePaletteFromUrl,
  serializePaletteToUrl,
  studioColorsToExportEntries,
} from '../../utils/studioPalette';
import type { StudioColor, HarmonyMode } from '../../utils/studioPalette';

const StudioPage: React.FC = () => {
  const { colors: urlColors } = useParams<{ colors: string }>();
  const navigate = useNavigate();

  // ── State ──────────────────────────────────────
  const [colors, setColors] = useState<StudioColor[]>(() =>
    parsePaletteFromUrl(urlColors || '264653-2A9D8F-E9C46A-F4A261-E76F51'),
  );
  const [harmonyMode, setHarmonyMode] = useState<HarmonyMode>('random');
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [dragFrom, setDragFrom] = useState<number | null>(null);
  const [dragOver, setDragOver] = useState<number | null>(null);
  const [shareTooltip, setShareTooltip] = useState(false);

  // Debounced URL sync
  const urlSyncTimer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => {
    clearTimeout(urlSyncTimer.current);
    urlSyncTimer.current = setTimeout(() => {
      const serialized = serializePaletteToUrl(colors);
      if (urlColors) navigate(`/studio/${serialized}`, { replace: true });
    }, 300);
    return () => clearTimeout(urlSyncTimer.current);
  }, [colors, navigate, urlColors]);

  // RouteMetadata owns the generator's title, description and URL policy.

  // ── Handlers ───────────────────────────────────
  const handleGenerate = useCallback(() => {
    setColors(prev => regeneratePalette(prev, harmonyMode));
  }, [harmonyMode]);

  const handleToggleLock = useCallback((index: number) => {
    setColors(prev =>
      prev.map((c, i) => i === index ? { ...c, locked: !c.locked } : c),
    );
  }, []);

  const handleColorChange = useCallback((index: number, hex: string) => {
    setColors(prev =>
      prev.map((c, i) => i === index ? { ...c, hex } : c),
    );
  }, []);

  const handleCountChange = useCallback((delta: number) => {
    setColors(prev => {
      const newCount = Math.min(8, Math.max(3, prev.length + delta));
      if (newCount > prev.length) {
        const extra = generateRandomPalette(newCount - prev.length);
        return [...prev, ...extra];
      }
      return prev.slice(0, newCount);
    });
  }, []);

  const handleDrop = useCallback((toIndex: number) => {
    if (dragFrom !== null && dragFrom !== toIndex) {
      setColors(prev => reorderPalette(prev, dragFrom, toIndex));
    }
    setDragFrom(null);
    setDragOver(null);
  }, [dragFrom]);

  const handleShare = useCallback(async () => {
    try {
      if (!await copyResult(window.location.origin + '/studio/' + serializePaletteToUrl(colors), 'studio', 'share')) throw new Error('Clipboard unavailable');
      setShareTooltip(true);
      setTimeout(() => setShareTooltip(false), 2000);
    } catch { window.prompt('Copy this palette link:', window.location.origin + '/studio/' + serializePaletteToUrl(colors)); }
  }, [colors]);

  // ── Keyboard shortcuts ─────────��───────────────
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName;
      if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA' || tag === 'BUTTON' || tag === 'A') return;

      if (e.code === 'Space') {
        e.preventDefault();
        setColors(prev => regeneratePalette(prev, harmonyMode));
        return;
      }
      if (e.key === 'l' || e.key === 'L') {
        setActiveIndex(prev => {
          if (prev !== null) {
            setColors(cs => cs.map((c, i) => i === prev ? { ...c, locked: !c.locked } : c));
          }
          return prev;
        });
        return;
      }
      if (e.key === 'ArrowRight') {
        setActiveIndex(prev =>
          prev === null ? 0 : Math.min(prev + 1, colors.length - 1),
        );
        return;
      }
      if (e.key === 'ArrowLeft') {
        setActiveIndex(prev =>
          prev === null ? 0 : Math.max(prev - 1, 0),
        );
        return;
      }
      if (e.key === 'Escape') {
        setShowExportModal(false);
        setActiveIndex(null);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [harmonyMode, colors.length]);

  // ── Export data ─────────────────────────────────
  const exportEntries = useMemo(
    () => studioColorsToExportEntries(colors),
    [colors],
  );

  return (
    <Layout maxWidth="max-w-6xl" noPadding>
      <div className="flex flex-col md:min-h-[40rem] md:h-[calc(100dvh-4rem)]">
        {/* Toolbar */}
        <div className="site-container py-5 sm:py-6"><h1 className="page-title">Palette Studio</h1></div>
        <StudioToolbar
          harmonyMode={harmonyMode}
          colorCount={colors.length}
          onGenerate={handleGenerate}
          onHarmonyChange={setHarmonyMode}
          onCountChange={handleCountChange}
          onExport={() => setShowExportModal(true)}
          onShare={handleShare}
        />

        {/* Share tooltip */}
        {shareTooltip && (
          <div className="absolute top-24 right-4 z-50 bg-zinc-800 text-white text-xs px-3 py-1.5 rounded-lg shadow-lg animate-fade-in">
            URL copied!
          </div>
        )}

        {/* Color bars */}
        <div className="flex-1 flex flex-col md:flex-row min-h-0">
          {colors.map((color, i) => (
            <StudioColorBar
              key={color.id}
              hex={color.hex}
              locked={color.locked}
              index={i}
              isActive={activeIndex === i}
              onToggleLock={() => handleToggleLock(i)}
              onColorChange={hex => handleColorChange(i, hex)}
              onSelect={() => setActiveIndex(i)}
              onDragStart={() => setDragFrom(i)}
              onDragOver={() => setDragOver(i)}
              onDrop={() => handleDrop(i)}
              isDragOver={dragOver === i && dragFrom !== i}
            />
          ))}
        </div>
      </div>

      {/* Export modal */}
      <CodeExportModal
        colors={exportEntries}
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
      />
    </Layout>
  );
};

export default StudioPage;
